import React, { useState } from "react";
import { useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { passwordSchema } from "@/lib/schemas/userSchemas";
import UsersRepository from "../repositories/UsersRepository";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, "Enter your current password"),
    newPassword: passwordSchema,
    confirmPassword: z.string().min(1, "Please confirm the password"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type ChangePasswordFormValues = z.infer<typeof changePasswordSchema>;

const REQUIREMENTS = [
  { label: "At least 8 characters", test: (v: string) => v.length >= 8 },
  { label: "At least one uppercase letter", test: (v: string) => /[A-Z]/.test(v) },
  { label: "At least one number", test: (v: string) => /[0-9]/.test(v) },
  { label: "At least one symbol", test: (v: string) => /[^a-zA-Z0-9]/.test(v) },
];

const ChangePasswordPage: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const usersRepository = new UsersRepository();

  const form = useForm<ChangePasswordFormValues>({
    defaultValues: { currentPassword: "", newPassword: "", confirmPassword: "" },
    resolver: zodResolver(changePasswordSchema),
  });

  const newPasswordValue = form.watch("newPassword");

  const onSubmit = async (data: ChangePasswordFormValues) => {
    try {
      await usersRepository.changeMyPassword(data.currentPassword, data.newPassword);
      logout();
      toast.success("Password changed — please sign in with your new password");
      navigate("/login");
    } catch (error: any) {
      const errors = error?.response?.data?.errors;
      if (errors?.CurrentPassword || errors?.currentPassword) {
        form.setError("currentPassword", { message: "The current password is not correct" });
        return;
      }
      form.setError("root", {
        message: "Failed to change password. Please try again.",
      });
    }
  };

  return (
    <div className="relative flex flex-1 items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 dark:to-primary/10 p-4">
      <div className="w-full max-w-sm animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex flex-col items-center mb-6">
          <h1 className="text-xl font-bold tracking-tight">Set new password</h1>
          <p className="text-sm text-muted-foreground mt-1 text-center">
            Enter the password you signed in with, then choose a new one. You are signed out afterwards.
          </p>
        </div>
        <Card className="w-full">
          <CardContent className="pt-6 space-y-4">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="currentPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Current password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          autoComplete="current-password"
                          disabled={form.formState.isSubmitting}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showNewPw ? "text" : "password"}
                            autoComplete="new-password"
                            disabled={form.formState.isSubmitting}
                            className="pr-9"
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground"
                            tabIndex={-1}
                            onClick={() => setShowNewPw((v) => !v)}
                          >
                            {showNewPw ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                      {newPasswordValue.length > 0 && (
                        <ul className="mt-1 space-y-0.5">
                          {REQUIREMENTS.map((req) => (
                            <li
                              key={req.label}
                              className={`text-xs ${req.test(newPasswordValue) ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}
                            >
                              {req.test(newPasswordValue) ? "✓" : "·"} {req.label}
                            </li>
                          ))}
                        </ul>
                      )}
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showConfirmPw ? "text" : "password"}
                            autoComplete="new-password"
                            disabled={form.formState.isSubmitting}
                            className="pr-9"
                            {...field}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground"
                            tabIndex={-1}
                            onClick={() => setShowConfirmPw((v) => !v)}
                          >
                            {showConfirmPw ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Separator />
                <Button
                  type="submit"
                  className="w-full"
                  disabled={form.formState.isSubmitting}
                >
                  {form.formState.isSubmitting && <Loader2 className="mr-2 size-4 animate-spin" />}
                  Set new password
                </Button>
                {form.formState.errors.root && (
                  <Alert variant="destructive">
                    <AlertDescription>{form.formState.errors.root.message}</AlertDescription>
                  </Alert>
                )}
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
